import { useState } from "react";
import { ShoppingBag, Trash2, Plus, Minus, Wallet, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { cartManual } from "../lib/api";
import { CartPayPalButtons } from "./CartPayPalButtons";

const DELIVERY_FEE = 5; // display only; server (DELIVERY_FEE_USD) is authoritative
// PayPal-only processing fee, mirrored server-side. final = (total + fixed) / (1 - percent)
const PAYPAL_FEE_PERCENT = 0.0349;
const PAYPAL_FEE_FIXED = 0.49;

export const Basket = () => {
  const { items, removeItem, setQty, clear, count, subtotal } = useCart();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", fulfillment: "pickup", address: "" });
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cartPayload = () => items.map((i) => ({ id: i.id, size: i.size, quantity: i.qty }));
  const isDelivery = form.fulfillment === "delivery";
  const deliveryFee = isDelivery ? DELIVERY_FEE : 0;
  const total = subtotal + deliveryFee;
  // Final amount charged through PayPal (order total grossed up for PayPal's fee).
  const paypalTotal = Math.round(((total + PAYPAL_FEE_FIXED) / (1 - PAYPAL_FEE_PERCENT)) * 100) / 100;

  const validContact = () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return false;
    }
    if (isDelivery && !form.address.trim()) {
      toast.error("Please enter your delivery address.");
      return false;
    }
    return true;
  };

  const payManual = async (method) => {
    if (!validContact()) return;
    setBusy(true);
    try {
      const res = await cartManual({ items: cartPayload(), method, ...form });
      setManual(res);
      toast.success(`Reference ${res.reference} created.`);
    } catch (e) {
      toast.error(e.message || "Could not create order.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="basket-button"
        onClick={() => setOpen(true)}
        aria-label="Open basket"
        className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-terracotta hover:bg-terracotta-hover text-white shadow-lg flex items-center justify-center transition-transform hover:scale-105"
      >
        <ShoppingBag size={22} />
        {count > 0 && (
          <span
            data-testid="basket-count"
            className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1 rounded-full bg-olive text-cream text-xs font-bold flex items-center justify-center border-2 border-cream"
          >
            {count}
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setManual(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-creamSoft">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ink">Your Basket</DialogTitle>
          </DialogHeader>

          {items.length === 0 ? (
            <p className="py-10 text-center text-[#787069]" data-testid="basket-empty">
              Your basket is empty. Add some treats from the menu!
            </p>
          ) : manual ? (
            <div className="py-4 text-center" data-testid="basket-manual">
              <CheckCircle2 size={44} className="mx-auto text-olive" />
              <h3 className="mt-3 font-serif text-xl text-ink">Order reference {manual.reference}</h3>
              <p className="mt-2 text-sm text-[#524B46]">
                Amount due: <strong>${manual.amount.toFixed(2)}</strong> via {manual.method}.
              </p>
              {manual.instructions?.zelle_email && (
                <div className="mt-4 bg-white rounded-xl border border-[#EFE9E0] p-4 flex items-center justify-between">
                  <span className="text-sm text-ink">{manual.instructions.zelle_email}</span>
                  <button onClick={() => { navigator.clipboard.writeText(manual.instructions.zelle_email); toast.success("Copied"); }}>
                    <Copy size={16} className="text-olive" />
                  </button>
                </div>
              )}
              {manual.instructions?.paypal_url && (
                <a href={manual.instructions.paypal_url} target="_blank" rel="noreferrer"
                  className="mt-4 inline-block text-olive underline">Open PayPal</a>
              )}
              <p className="mt-3 text-xs text-[#787069]">{manual.instructions?.note}</p>
              <Button onClick={() => { clear(); setManual(null); setOpen(false); }}
                className="mt-6 w-full rounded-full bg-olive hover:bg-olive-hover text-cream">
                Done
              </Button>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="space-y-3">
                {items.map((i) => (
                  <div key={i.key} data-testid={`basket-item-${i.id}`}
                    className="flex items-center gap-3 bg-white rounded-xl border border-[#EFE9E0] p-3">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-ink leading-tight">{i.name}</p>
                      <p className="text-xs text-[#787069]">{i.size} · ${i.price.toFixed(2)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setQty(i.key, i.qty - 1)} className="h-7 w-7 rounded-full border border-[#E8E2D9] flex items-center justify-center text-ink"><Minus size={13} /></button>
                      <span className="w-6 text-center text-sm font-medium">{i.qty}</span>
                      <button onClick={() => setQty(i.key, i.qty + 1)} className="h-7 w-7 rounded-full border border-[#E8E2D9] flex items-center justify-center text-ink"><Plus size={13} /></button>
                    </div>
                    <span className="w-16 text-right text-sm font-semibold text-ink">${(i.price * i.qty).toFixed(2)}</span>
                    <button onClick={() => removeItem(i.key)} aria-label="Remove"><Trash2 size={16} className="text-terracotta" /></button>
                  </div>
                ))}
              </div>

              <div className="border-t border-[#EFE9E0] pt-4 space-y-1.5">
                <div className="flex items-center justify-between text-sm text-[#524B46]">
                  <span>Subtotal</span>
                  <span data-testid="basket-subtotal">${subtotal.toFixed(2)}</span>
                </div>
                {isDelivery && (
                  <div className="flex items-center justify-between text-sm text-[#524B46]" data-testid="basket-delivery-fee">
                    <span>Delivery fee</span>
                    <span>${deliveryFee.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-1">
                  <span className="text-sm font-semibold text-ink">Total</span>
                  <span data-testid="basket-total" className="font-serif text-2xl text-ink">${total.toFixed(2)}</span>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="cart-name">Your Name</Label>
                  <Input id="cart-name" data-testid="cart-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="h-11 bg-white" placeholder="Jane Doe" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cart-email">Email</Label>
                  <Input id="cart-email" data-testid="cart-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="h-11 bg-white" placeholder="jane@email.com" />
                </div>
              </div>

              <div className="flex gap-2">
                {["pickup", "delivery"].map((o) => (
                  <button key={o} data-testid={`cart-fulfillment-${o}`} onClick={() => set("fulfillment", o)}
                    className={`flex-1 h-10 rounded-full text-sm font-medium border capitalize ${form.fulfillment === o ? "bg-olive text-cream border-olive" : "bg-white text-ink border-[#E8E2D9]"}`}>
                    {o === "delivery" ? "Local Delivery" : o}
                  </button>
                ))}
              </div>

              {isDelivery && (
                <div className="space-y-1.5">
                  <Label htmlFor="cart-address">Local Delivery Address</Label>
                  <textarea
                    id="cart-address"
                    data-testid="cart-address"
                    value={form.address}
                    onChange={(e) => set("address", e.target.value)}
                    rows={3}
                    placeholder="Street, city, state, ZIP"
                    className="w-full rounded-lg border border-[#E8E2D9] bg-white text-sm px-3 py-2 text-ink"
                  />
                  <p className="text-xs text-[#787069]" data-testid="cart-delivery-radius-note">Delivery is limited to a 12-mile radius from the specified zip code.</p>
                  <p className="text-xs text-[#787069]">A ${DELIVERY_FEE.toFixed(2)} delivery fee is added to your total.</p>
                </div>
              )}

              <Tabs defaultValue="paypal" className="w-full">
                <TabsList className="grid grid-cols-2 w-full">
                  <TabsTrigger value="paypal" data-testid="cart-tab-paypal">PayPal</TabsTrigger>
                  <TabsTrigger value="zelle" data-testid="cart-tab-zelle">Zelle</TabsTrigger>
                </TabsList>
                <TabsContent value="paypal" className="pt-4">
                  <div data-testid="cart-paypal-fee-note" className="mb-4 rounded-lg bg-[#F3EFE7] px-3 py-2 text-center">
                    <p className="text-xs text-[#787069]">A transaction fee will be applied to the total amount.</p>
                    <p className="mt-1 text-sm font-semibold text-ink" data-testid="cart-paypal-final">{`You'll pay $${paypalTotal.toFixed(2)} via PayPal`}</p>
                  </div>
                  <CartPayPalButtons
                    validate={validContact}
                    getPayload={() => ({ items: cartPayload(), ...form })}
                    onPaid={(orderId) => { clear(); setOpen(false); window.location.href = `/payment/success?session_id=${orderId}`; }}
                  />
                </TabsContent>
                <TabsContent value="zelle" className="pt-4">
                  <p className="text-sm text-[#524B46] flex items-start gap-2"><Wallet size={18} className="text-olive mt-0.5" /> Pay by Zelle bank transfer for your ${total.toFixed(2)} order.</p>
                  <Button onClick={() => payManual("zelle")} disabled={busy} data-testid="cart-pay-zelle"
                    className="mt-4 w-full h-12 rounded-full bg-olive hover:bg-olive-hover text-cream">
                    Get Zelle Details
                  </Button>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
